/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { ITasksConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { TaskService } from 'app/core/services/task-service';
import { QueueService } from 'app/core/services/queue-service';
import { TaskWithLocalState } from 'app/core/models/local-state-extensions';
import { ApiDate } from 'app/shared/models/essette-api';
import { I18N } from 'aurelia-i18n';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { spliceCopy } from 'app/shared/lib/utilities';

@autoinject()
export class AddTasksSelected extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    /** Array of tasks the users has selected (standard and custom) to be saved */
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected insertTasks: TaskWithLocalState[] = [];

    @bindable.object({ defaultBindingMode: bindingMode.oneTime })
    protected filteredTasks: TaskWithLocalState[] = [];

    @bindable
    protected currentTasks: Core.Task[] = [];

    @bindable.objectNull
    protected tasksConfig: ITasksConfig | null = null;

    @bindable.objectNull
    protected queuesConfig: IQueuesConfig | null = null;

    /** array of tasks user has selected from either the multi-select or autocomplete */
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected selectedTasks: Core.TaskDefinition[] = [];
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected selectedRecommendedTasks: Core.TaskDefinition[] = [];

    @bindable
    protected forceDisallowAddToLibrary: boolean = false;

    /** Used to show copy all on delegated to user if all queues are the same or blank */
    protected allQueuesBlankOrSame: boolean = false;

    /** queue lookup (bound to select) */
    protected queues: ReadonlyArray<Core.Queue> = [];
    protected queueProviders: ReadonlyArray<CD.Provider> = [];

    /** Used to show summary about smart tasks/portal queues when there is a validation error */
    public hasInvalidQueue: boolean = false;

    /** Permission that controls whether a custom task can be added to the library (N/A to interventions) */
    protected hasAddCustomTaskToLibrary: boolean = false;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly taskService: TaskService,
        private readonly queueService: QueueService,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.tasksConfig) { throw new Error('Bindable property [tasks-config] was not set'); }
        if(!this.queuesConfig) { throw new Error('Bindable property [queues-config] was not set'); }

        this.taskService.configure(this.tasksConfig);
        this.queueService.configure(this.queuesConfig);

        this.checkPermissions();

        this.loadLookups();
    }

    ////****************** Events ***************************************************////

    protected delegateToChanged(changedTask: Core.Task | null): void {
        if(!changedTask?.delegated?.queue) { return; }

        const queue = changedTask.delegated.queue;
        if(!queue.delegated?.type) { return; }

        if(changedTask.delegated?.to) {
            changedTask.delegated.to.type = {
                id: queue.delegated.type.id,
                code: queue.delegated.type.code
            };
        }
    }

    protected async delegateQueueChanged(changedTask: Core.Task | null): Promise<void> {
        if(this.insertTasks.length === 0) { this.allQueuesBlankOrSame = true; }
        let firstSelectedQueue: Core.Queue | null = null;
        if(this.insertTasks[0]?.delegated?.queue) {
            firstSelectedQueue = this.insertTasks[0].delegated.queue || null;
        }

        if(firstSelectedQueue) {
            this.allQueuesBlankOrSame = this.insertTasks.every(t => {
                const queueId = t.delegated?.queue?.id;
                return queueId === firstSelectedQueue!.id || !queueId;
            });
        }
        else {
            this.allQueuesBlankOrSame = false;
        }

        if(changedTask?.delegated?.to) {
            changedTask.delegated.to = null;
        }
        const queueType = changedTask?.delegated?.queue?.delegated?.type?.code;
        if(changedTask?.delegated?.queue?.id && changedTask.delegated.queue.users === undefined &&
            queueType !== 'CD.Provider') {
            //WARNING: even if the queue's type is Intervention, if the queue has been allowed to "All Authenticated Users" -
            //user can in theory send this Intervention to some user without CM access.
            //This is up to the client to tweak.
            changedTask.delegated.queue.users = await this.loadQueueUsers(changedTask?.delegated?.queue?.id);
        }
        // If Provider Portal is selected and providers have yet to be loaded, load providers
        else if(queueType === 'CD.Provider' && this.queueProviders.length === 0) {
            await this.loadProviders();
        }
        this.updateValidation();
    }

    public async insertTasksChanged(newInsertTasks: TaskWithLocalState[] | null, oldInsertTasks: TaskWithLocalState[] | null): Promise<void> {
        // Return if no tasks selected or if a task was removed
        if(this.insertTasks.length === 0 || newInsertTasks == null || newInsertTasks.length <= (oldInsertTasks?.length ?? 0)) {
            this.hasInvalidQueue = false;
            return;
        };

        // Traverse over all recently added tasks and load queue users for each
        const addedTasks = (oldInsertTasks == null) ? newInsertTasks : newInsertTasks.filter(nit => !oldInsertTasks.some(oit => oit.description == nit.description));
        for(let i = 0; i < addedTasks.length; i++) {
            const j = this.insertTasks.indexOf(addedTasks[i]);
            if(j !== -1 && this.insertTasks[j].delegated?.queue?.id) {
                const queueId = this.insertTasks[j].delegated?.queue?.id;
                const users = await this.loadQueueUsers(queueId!);
                this.insertTasks[j].delegated!.queue!.users = users;
            }
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        const targetType = this.tasksConfig?.targetType;
        if(!targetType) { return; }

        await this.accountContext.load();
        if(targetType === 'CM.Case') {
            this.hasAddCustomTaskToLibrary = this.accountContext.hasPermission('CM.CaseTask.Insert.AddToLibrary');
        }
        else if(targetType === 'CM.CarePlanGoal') {
            this.hasAddCustomTaskToLibrary = this.accountContext.hasPermission('CM.CarePlanIntervention.Insert.AddToLibrary');
        }
    }

    /** Called when a user removes a task from the grid. Will ultimately trigger the selectedTasksChanged() method */
    protected removeTask(task: Core.Task): void {
        if(task.isCustom) {
            // Custom tasks are not entries in selectedTasks, so no need to find and remove it
            const index = this.insertTasks.indexOf(task);
            if(index >= 0) {
                this.insertTasks = spliceCopy(this.insertTasks, index, 1);
            }
        }
        else {
            // Find the task in the selectedTasks or selectedRecommendedTasks array whence to remove.
            // Either will trigger the selectedTasksChanged() or selectedRecommendedTasksChanged()), which will update insertTasks
            let st = this.selectedTasks.find(t => t.id === (task.definition && task.definition.id));
            if(st) {
                this.selectedTasks.splice(this.selectedTasks.indexOf(st), 1);
            } else {
                st = this.selectedRecommendedTasks.find(t => t.id === (task.definition && task.definition.id));
                if(st) {
                    this.selectedRecommendedTasks.splice(this.selectedRecommendedTasks.indexOf(st), 1);
                }
            }
        }
    }

    protected copyDelegatedQueue(queue: Core.Queue | null): void {
        this.filterTasks(this.insertTasks).forEach(t => {

            if(!t.delegated) {
                t.delegated = {};
            }
            t.delegated.queue = queue;
            t.delegated.to = null;
        });

        this.allQueuesBlankOrSame = true;
        this.updateValidation();
    }

    protected copyDelegatedTo(delegateTo: Core.TaskDelegateTargetModel | null): void {
        this.filterTasks(this.insertTasks).forEach(t => {

            if(!t.delegated) {
                t.delegated = {};
            }
            t.delegated.to = delegateTo;
            this.delegateToChanged(t);
        });
    }

    protected copyDueDate(date: ApiDate | null): void {
        this.filterTasks(this.insertTasks).forEach(t => {

            if(!t.due) {
                t.due = {};
            }
            t.due.date = date;
        });
    }

    protected updateValidation(): void {
        for(const task of this.insertTasks) {
            const result = this.isValidateDelegatedQueue(task, task.delegated?.queue || null);

            if(!result) {
                this.hasInvalidQueue = true;
                return;
            }
        }
        this.hasInvalidQueue = false;
    }

    protected isValidateDelegatedQueue(task: Core.Task | null, queue: Core.Queue | null): boolean {
        if(!task || !queue) { return true; }

        // smart task assigned to portal queue is invalid
        const taskType = task?.definition?.type?.code || null;
        const isSmartTask: boolean = !!(taskType && taskType !== 'Core.Task');
        const isPortalQueue: boolean = queue.delegated?.type?.code === 'CD.Provider';
        return !(isSmartTask && isPortalQueue);
    }

    protected isDuplicate(task: TaskWithLocalState) {
        return this.currentTasks.some(t => (task.definition && task.definition.id) === (t.definition && t.definition.id));
    }

    protected getDuplicateText(): string {
        if(this.i18n && this.tasksConfig) {
            return this.i18n.tr('ui:labels.duplicate', { what: this.i18n.tr(this.tasksConfig.labelKey) })
        }
        return '';
    }

    public filterTasks(tasks: Core.Task[]): Core.Task[] {
        if(!this.filteredTasks || this.filteredTasks.length === 0) return tasks;
        return tasks.filter(t => this.filteredTasks.some(ft => ft.externalIdentifier === t.externalIdentifier));
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    public async loadLookups(): Promise<void> {

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = [
            'delegated',
            'type'
        ];
        const fields = [
            'delegated.type.code',
            'description'
        ];

        try {
            this.state.beginLoad();

            const queues = await this.queueService.loadQueues(null, options, includes, fields);

            this.queues = queues.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadQueueUsers(queueId: string): Promise<Array<Core.User>> {
        try {
            this.state.beginLoad();
            const options: Partial<IQueryLoadOptions> = {
                all: true
            };
            const fields = ['description'];
            const response = await this.queueService.loadQueueUsers(queueId, options, undefined, fields);
            return response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadProviders(): Promise<void> {
        try {
            this.state.beginLoad();
            const options: Partial<IQueryLoadOptions> = {
                all: true
            };
            const fields = ['description'];
            const memberId = this.tasksConfig?.memberId || '';
            const response = await this.queueService.loadPortalProviders(memberId, options, undefined, fields)
            this.queueProviders = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
